import { useRef, useState, type KeyboardEvent } from 'react';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export function NoteEditForm({
  initialBody,
  isSaving,
  onSave,
  onCancel,
  maxLength,
}: {
  initialBody: string;
  isSaving: boolean;
  onSave: (body: string) => Promise<void>;
  onCancel: () => void;
  maxLength?: number;
}) {
  const gt = useGT();
  const [text, setText] = useState(initialBody);
  const canSave = text.trim().length > 0 && !isSaving;
  const inFlight = useRef(false);

  async function save(): Promise<void> {
    if (!canSave || inFlight.current) return;
    inFlight.current = true;
    await onSave(text).finally(() => {
      inFlight.current = false;
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (
      event.key !== 'Enter' ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    ) {
      return;
    }
    event.preventDefault();
    void save();
  }

  return (
    <div className="mt-1 space-y-1.5">
      <Textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={2}
        maxLength={maxLength}
        className="h-12 min-h-12 w-full resize-y overflow-auto text-xs"
        onKeyDown={handleKeyDown}
        aria-label={gt('Edit note', {
          context: 'aria label for the note editor textarea',
        })}
        disabled={isSaving}
      />
      <div className="flex gap-1.5">
        <Button
          size="sm"
          className="h-6 text-xs"
          disabled={!canSave}
          onClick={() => void save()}
        >
          <T context="button that saves an edited note">Save</T>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 text-xs"
          disabled={isSaving}
          onClick={onCancel}
        >
          <T context="button that cancels editing a note">Cancel</T>
        </Button>
      </div>
    </div>
  );
}
